/**
 * Reaction emoji, in display order.
 *
 * Positional: the index is what the API stores in the row key, so append only
 * — never reorder or remove, or existing reactions re-label themselves. Must
 * match EMOJI in api/shared/store.js.
 */
export const REACTIONS = ["👍", "🔥", "😂", "😮", "❤️"] as const;

export type Reaction = (typeof REACTIONS)[number];

/** postId -> emoji -> count */
export type ReactionCounts = Record<string, Record<string, number>>;

/** Spoken by screen readers in place of the bare glyph. */
export const REACTION_LABEL: Record<Reaction, string> = {
  "👍": "agree",
  "🔥": "great",
  "😂": "funny",
  "😮": "surprising",
  "❤️": "love",
};

const CLIENT_KEY = "wall.client";
const MINE_KEY = "wall.reactions";

/**
 * A random per-browser id so the same person reacting twice is a no-op and can
 * take a reaction back. Not an account and not a fingerprint — it identifies
 * nothing about the visitor, and clearing site data simply forgets it.
 */
export function clientId(): string {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    // Private mode: reactions still register, they just will not survive a reload.
    return "anon";
  }
}

/** Which reactions this browser has made, so the buttons render pressed. */
export function readMine(): Record<string, string[]> {
  try {
    return JSON.parse(localStorage.getItem(MINE_KEY) || "{}");
  } catch {
    return {};
  }
}

export function writeMine(mine: Record<string, string[]>): void {
  try {
    localStorage.setItem(MINE_KEY, JSON.stringify(mine));
  } catch {
    /* nothing to do — the server still has the reaction */
  }
}
